import React, { useRef, useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'
import PerfectScrollbar from 'react-perfect-scrollbar'
import clsx from 'clsx'

import lang from '@/lang'
import IconZoomMinus from '@assets/images/icons/zoom_minus.svg'
import IconZoomPlus from '@assets/images/icons/zoom_plus.svg'
import IconDownload from '@assets/images/icons/download.svg'
import Panel from '@/components/layouts/panel/index'
import PanelHeader from '@/components/layouts/panel/panel-header'
import { DocumentControlButton } from '@/modules/base/components/buttons/index'
import PanelContent from '@/components/layouts/panel/panel-content'
import LetterPaperThumbnail from '@/modules/letter-paper-settings/letter-paper/letter-paper-thumbnail'
import debounce from 'lodash/debounce'
import { downloadFile } from '@/modules/_helpers/downloadFile'

const useStyles = makeStyles(theme => ({
  fileContent: {
    height: '100%',
    backgroundColor: theme.white,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    '& svg path': {
      fillOpacity: 0,
    },
  },
  fileContentOverlay: {
    backgroundColor: 'transparent',
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    backgroundColor: theme.white,
    '& > *': {
      margin: '20px 0px 20px 20px',
    },
    '& a': {
      border: theme.border,
      backgroundColor: theme.white,
      minWidth: 45,
      minHeight: 45,
    },
    '& a:hover': {
      backgroundColor: theme.backgroundColor,
    },
  },
  button: {
    height: 40,
    maxHeight: 40,
    width: 40,
    maxWidth: 40,
    transition: '.2s',
  },
}))

const LetterPaperPreview = (props) => {
  const classes = useStyles(props)
  const { url, id, className, forceFileReload } = props
  const panelContentRef = useRef(null)
  const [width, setWidth] = useState(0)
  const [pageScale, setPageScale] = useState(1)

  const ids = Array.isArray(id) ? id : [id]
  const urls = Array.isArray(url) ? url : [url]

  const downloadUrl = props.downloadUrl || urls[0]

  useEffect(() => {
    const setWidthDebounce = debounce((newWidth) => setWidth(newWidth - 20), 200)
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        setWidthDebounce(entry.contentRect.width)
      }
    })

    resizeObserver.observe(panelContentRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  const zoomInHandle = useCallback(() => {
    setPageScale(currentPageScale => currentPageScale + 0.1)
  }, [])

  const zoomOutHandle = useCallback(() => {
    setPageScale(currentPageScale => currentPageScale - 0.1)
  }, [])

  const onDownloadHandle = useCallback(() => {
    downloadFile(downloadUrl)
  }, [downloadUrl])

  const items = urls.map((itemUrl, index) => {
    return (
      <LetterPaperThumbnail
        key={ids[index]}
        id={ids[index]}
        forceFileReload={forceFileReload}
        className={clsx(classes.fileContent, index > 0 && classes.fileContentOverlay)}
        url={itemUrl}
        width={width}
        pageScale={pageScale}
        showMaxPages={-1}
        pageVertical={true}
        renderTextLayer={false}
        renderMode={index > 0 ? 'svg' : 'canvas'}
      />
    )
  },
  )

  return (
    <Panel className={className} >
      <PanelHeader toolbarRender={() => (
        <div className={classes.previewHeader}>
          <DocumentControlButton
            icon={IconZoomMinus}
            title={lang.expense.previewToolbar.items.zoomOut}
            customClass={classes.button}
            onClick={zoomOutHandle} />
          <DocumentControlButton
            icon={IconZoomPlus}
            title={lang.expense.previewToolbar.items.zoomIn}
            customClass={classes.button}
            onClick={zoomInHandle} />
          <DocumentControlButton
            icon={IconDownload}
            title={lang.settings.letterPaperSettings.downloadLetterPaperPreviewLabel}
            customClass={classes.button}
            onClick={onDownloadHandle} />
        </div>
      )} />
      <PanelContent ref={panelContentRef}>
        <PerfectScrollbar>
          {items}
        </PerfectScrollbar>
      </PanelContent>
    </Panel>
  )
}

LetterPaperPreview.propTypes = {
  url: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  downloadUrl: PropTypes.string,
  forceFileReload: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  className: PropTypes.string,
}

export default React.memo(LetterPaperPreview)
